import { useCallback, useMemo } from 'react';

import { Stack } from '../../../../../components/Stack';
import { useStepWizard } from '../../../../../components/StepWizard';
import { TextEditor } from '../../../../../components/TextEditor';
import { useClassHelper } from '../../../../../hooks';
import { DATADOG_ACTIONS } from '../../../constants';
import { isTextChildrenEmpty } from '../../../helpers';
import { trackSdkCall } from '../../../helpers/trackSdkCall';
import { useDataDogActions } from '../../../hooks/useDataDogActions';
import { componentName } from '../constants';
import { useResetScrollOnMount } from '../hooks/useResetScrollOnMount';
import { useVoWifiRenderTracking } from '../hooks/useVoWifiRenderTracking';
import { PageProps, VoWifiTermsConditions } from '../types';
import { Button } from './shared/Button';
import { HeaderImage } from './shared/HeaderImage';
import { Title } from './shared/Title';

const defaultTexts = {
  title: 'Terms & Conditions',
  content: [],
  confirm: 'Accept',
} as const;

interface Props
  extends PageProps,
    Partial<Omit<VoWifiTermsConditions, 'type'>> {
  /**
   * Land two steps ahead instead of one. The template sets it when the step
   * after this one is the address form and the state already carries a
   * complete address; the form stays mounted so "Edit Address" can step back.
   */
  skipNextStep?: boolean;
}

export const TermsConditionsScreen = ({
  themeValues,
  isPortalPreview,
  title,
  content,
  confirm,
  reject,
  deviceSdk,
  skipNextStep = false,
}: Props) => {
  const c = useClassHelper(componentName);

  const { actions, dataDogLogger } = useDataDogActions({
    isPortalPreview,
    actionKey: 'vowifi',
  });

  const { containerRef, onImageError } = useVoWifiRenderTracking(
    isPortalPreview,
    'terms',
  );

  const { nextStep, goToStep, activeStep } = useStepWizard();

  // Reset scroll on mount
  useResetScrollOnMount();

  const handleReject = useCallback(() => {
    if (isPortalPreview) {
      return;
    }

    if (deviceSdk && deviceSdk?.voWifiActivationCancel) {
      // cancel emits its own teardown action; stop the session after so that
      // log is captured in it.
      trackSdkCall(
        'voWifiActivationCancel',
        () => deviceSdk.voWifiActivationCancel(),
        dataDogLogger,
      );
      dataDogLogger.stopSession();
      return;
    }

    // The SDK method is missing — decline does nothing. Report the silent
    // failure, then close the session.
    dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
      flow: 'sdk_unavailable',
      errorCode: 'SDK_METHOD_MISSING',
      errorMessage: 'deviceSdk.voWifiActivationCancel is unavailable',
      method: 'voWifiActivationCancel',
    });
    dataDogLogger.stopSession();
  }, [deviceSdk]);

  const handleConfirm = useCallback(() => {
    if (isPortalPreview) {
      return;
    }

    if (skipNextStep) {
      goToStep(activeStep + 2);
      return;
    }

    nextStep();
  }, [isPortalPreview, skipNextStep, goToStep, activeStep, nextStep]);

  const isContactUsTextEmpty = useMemo(() => {
    return isTextChildrenEmpty(content);
  }, [content]);

  return (
    <div ref={containerRef} className={c('__termsConditions-screen')}>
      <section className={c('__termsConditions-screen-inner')}>
        <div className={c('__termsConditions-screen-header')}>
          <Stack direction="column" gap={8}>
            <HeaderImage
              src={themeValues?.logoUrl}
              isPortalPreview={isPortalPreview}
              onError={() => onImageError(themeValues?.logoUrl)}
            />
            <Title fontFamily={themeValues?.fontTitle}>
              {title ? title : defaultTexts?.title}
            </Title>
          </Stack>
        </div>

        <div
          className={c('__termsConditions-screen-body my-4', {
            '__termsConditions-screen-body--is-preview': isPortalPreview,
          })}
        >
          {!isContactUsTextEmpty && (
            <TextEditor
              viewOnly
              value={content}
              valueFontParagraph={themeValues.fontParagraph}
            />
          )}
        </div>

        <Stack
          direction="column"
          gap={4}
          className={c('__termsConditions-screen-footer')}
        >
          <Button
            type="button"
            onClick={handleConfirm}
            data-dd-action-name={actions?.terms_accept_button_clicked}
          >
            {confirm ? confirm : defaultTexts?.confirm}
          </Button>
          <Button
            type="button"
            onClick={handleReject}
            ghost
            data-dd-action-name={actions?.terms_decline_button_clicked}
          >
            {reject ? reject : 'Decline'}
          </Button>
        </Stack>
      </section>
    </div>
  );
};
